import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography
} from '@material-ui/core'
import React, { useEffect, useState } from 'react'

import {
  createPlannerProgramObservation,
  fetchPlannerObservationSessions,
  fetchPlannerObservationTeachers
} from 'api'
import { getLoggedInUserName, showToast } from 'app/helpers'

const today = () => new Date().toISOString().slice(0, 10)

const YesNo = ({ value, onChange }) => (
  <Box display="flex" style={{ gap: 4 }}>
    {['yes', 'no'].map((v) => (
      <Button
        key={v}
        size="small"
        variant={value === v ? 'contained' : 'outlined'}
        color={value === v ? 'primary' : 'default'}
        onClick={() => onChange(v)}
        style={{ minWidth: 44 }}
      >
        {v === 'yes' ? 'Yes' : 'No'}
      </Button>
    ))}
  </Box>
)

// Group questions by their (ordered) section label.
const groupBySection = (questions) => {
  const groups = []
  const idx = {}
  questions.forEach((q) => {
    const key = q.section || ''
    if (!(key in idx)) {
      idx[key] = groups.length
      groups.push({ section: key, items: [] })
    }
    groups[idx[key]].items.push(q)
  })
  return groups
}

const blank = () => ({
  teacher: '',
  observation_date: today(),
  schedule: '',
  answers: {},
  did_well: '',
  to_improve: ''
})

const sessionText = (s) =>
  [[s.start_time, s.end_time].filter(Boolean).join('–'), s.course_name, s.room]
    .filter(Boolean)
    .join(' · ')

/**
 * The non-preschool observation flow: pick a teacher, answer the program's Yes/No
 * questions (grouped by section), save. Observer is the logged-in user (set
 * server-side). Teacher list comes from the program's Course batches at the centre.
 */
const ProgramObservation = ({ centreId, program }) => {
  const [teachers, setTeachers] = useState([])
  const [sessions, setSessions] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadingSessions, setLoadingSessions] = useState(false)
  const [saving, setSaving] = useState(false)
  const [draft, setDraft] = useState(blank())
  // Persistent "submitted" confirmation shown after a successful save (the
  // transient toast alone was easy to miss — the user couldn't tell it saved).
  const [justSaved, setJustSaved] = useState(null)

  const questions = (program && program.questions) || []
  const groups = groupBySection(questions)
  const programId = program && program.uuid
  // Every question must be answered before saving.
  const allAnswered =
    questions.length === 0 || questions.every((q) => draft.answers[q.uuid])

  const load = async () => {
    if (!programId) return
    setLoading(true)
    try {
      const t = await fetchPlannerObservationTeachers({
        queries: { program: programId, centre: centreId }
      })
      setTeachers((t && t.teachers) || [])
    } catch (e) {
      showToast('Failed to load teachers', 'danger')
    }
    setLoading(false)
  }

  useEffect(() => {
    setDraft(blank())
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programId, centreId])

  // Load the teacher's sessions for the chosen date (the Session picker).
  useEffect(() => {
    if (!programId || !draft.teacher || !draft.observation_date) {
      setSessions([])
      return
    }
    setLoadingSessions(true)
    setDraft((d) => ({ ...d, schedule: '' })) // reset choice when teacher/date changes
    fetchPlannerObservationSessions({
      queries: {
        program: programId,
        teacher: draft.teacher,
        date: draft.observation_date,
        centre: centreId
      }
    })
      .then((r) => setSessions((r && r.sessions) || []))
      .catch(() => setSessions([]))
      .finally(() => setLoadingSessions(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programId, centreId, draft.teacher, draft.observation_date])

  const setAnswer = (qid, v) =>
    setDraft((d) => ({ ...d, answers: { ...d.answers, [qid]: v } }))

  const save = async () => {
    setSaving(true)
    // Capture the human-readable labels before the draft is reset.
    const savedTeacher =
      (teachers.find((t) => t.uuid === draft.teacher) || {}).name || 'Teacher'
    const savedSession = sessions.find((s) => s.uuid === draft.schedule) || {}
    try {
      await createPlannerProgramObservation({
        program: programId,
        teacher: draft.teacher || null,
        centre: centreId || null,
        observation_date: draft.observation_date,
        schedule: draft.schedule || null,
        answers: draft.answers,
        did_well: draft.did_well,
        to_improve: draft.to_improve
      })
      showToast('Observation saved', 'success')
      setJustSaved({
        teacher: savedTeacher,
        date: draft.observation_date,
        session:
          savedSession && savedSession.uuid ? sessionText(savedSession) : ''
      })
      setDraft(blank())
      await load()
    } catch (e) {
      showToast('Failed to save observation', 'danger')
    }
    setSaving(false)
  }

  if (!program) return null

  return (
    <Box mt={1}>
      {justSaved && (
        <Paper
          variant="outlined"
          style={{
            padding: 12,
            marginBottom: 12,
            borderRadius: 10,
            background: '#edf7ed',
            borderColor: '#4caf50'
          }}
        >
          <Typography
            variant="body2"
            style={{ color: '#1e4620', fontWeight: 600 }}
          >
            ✓ Observation submitted — {justSaved.teacher}
            {justSaved.date ? ` · ${justSaved.date}` : ''}
            {justSaved.session ? ` · ${justSaved.session}` : ''}
          </Typography>
          <Typography variant="caption" style={{ color: '#1e4620' }}>
            It now appears in the “All observations” tab. Pick a teacher to
            record another.
          </Typography>
        </Paper>
      )}
      <Paper variant="outlined" style={{ padding: 16, borderRadius: 12 }}>
        <Box display="flex" flexWrap="wrap" style={{ gap: 12 }} mb={1}>
          <FormControl
            variant="outlined"
            size="small"
            style={{ minWidth: 240 }}
          >
            <InputLabel>Teacher</InputLabel>
            <Select
              label="Teacher"
              value={draft.teacher}
              onChange={(e) => {
                const v = e.target.value
                setJustSaved(null)
                setDraft((d) => ({ ...d, teacher: v }))
              }}
            >
              {!teachers.length && (
                <MenuItem value="">
                  <em>
                    {centreId ? 'No teachers linked' : 'Pick a centre first'}
                  </em>
                </MenuItem>
              )}
              {teachers.map((t) => (
                <MenuItem key={t.uuid} value={t.uuid}>
                  {t.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            type="date"
            size="small"
            label="Observation date"
            InputLabelProps={{ shrink: true }}
            value={draft.observation_date}
            onChange={(e) => {
              const v = e.target.value
              setDraft((d) => ({ ...d, observation_date: v }))
            }}
          />
          {/* Observer = the logged-in Alfred user (set server-side); shown read-only. */}
          <TextField
            size="small"
            label="Observer (you)"
            value={getLoggedInUserName() || '—'}
            InputProps={{ readOnly: true }}
            helperText="Auto-filled from your login"
            style={{ minWidth: 200 }}
          />
        </Box>

        {/* Session — which of the teacher's sessions that day this is for. */}
        <Box mb={1}>
          <FormControl
            variant="outlined"
            size="small"
            fullWidth
            disabled={
              !draft.teacher || !draft.observation_date || loadingSessions
            }
          >
            <InputLabel>Session</InputLabel>
            <Select
              label="Session"
              value={draft.schedule}
              onChange={(e) => {
                const v = e.target.value
                setDraft((d) => ({ ...d, schedule: v }))
              }}
            >
              {sessions.map((s) => (
                <MenuItem key={s.uuid} value={s.uuid}>
                  {sessionText(s)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {loadingSessions && (
            <Typography variant="caption" color="textSecondary">
              Loading sessions…
            </Typography>
          )}
          {draft.teacher &&
            draft.observation_date &&
            !loadingSessions &&
            sessions.length === 0 && (
              <Typography variant="caption" color="textSecondary">
                No scheduled sessions for this teacher on this date — you can
                still save.
              </Typography>
            )}
        </Box>

        {loading ? (
          <CircularProgress />
        ) : (
          groups.map((g) => (
            <Box key={g.section || 'general'} mt={1.5}>
              {!!g.section && (
                <Typography
                  variant="overline"
                  style={{
                    color: '#9a8f82',
                    letterSpacing: 1,
                    fontWeight: 600
                  }}
                >
                  {g.section}
                </Typography>
              )}
              {g.items.map((q) => (
                <Box
                  key={q.uuid}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  mt={0.75}
                >
                  <Typography
                    variant="body2"
                    style={{ flex: 1, paddingRight: 12 }}
                  >
                    {q.text}
                  </Typography>
                  <YesNo
                    value={draft.answers[q.uuid]}
                    onChange={(v) => setAnswer(q.uuid, v)}
                  />
                </Box>
              ))}
            </Box>
          ))
        )}

        <Divider style={{ margin: '16px 0' }} />
        <TextField
          fullWidth
          multiline
          minRows={2}
          label="What did the teacher do very well?"
          value={draft.did_well}
          onChange={(e) => {
            const v = e.target.value
            setDraft((d) => ({ ...d, did_well: v }))
          }}
        />
        <TextField
          fullWidth
          multiline
          minRows={2}
          style={{ marginTop: 12 }}
          label="What does the teacher need to improve upon?"
          value={draft.to_improve}
          onChange={(e) => {
            const v = e.target.value
            setDraft((d) => ({ ...d, to_improve: v }))
          }}
        />

        <Box mt={2}>
          <Button
            color="primary"
            variant="contained"
            onClick={save}
            disabled={
              saving ||
              !draft.teacher ||
              (sessions.length > 0 && !draft.schedule) ||
              !allAnswered
            }
          >
            {saving ? 'Saving…' : 'Save observation'}
          </Button>
          {(!draft.teacher ||
            (sessions.length > 0 && !draft.schedule) ||
            !allAnswered) && (
            <Typography
              variant="caption"
              color="textSecondary"
              style={{ marginLeft: 10 }}
            >
              {!draft.teacher
                ? 'Pick a teacher to save.'
                : sessions.length > 0 && !draft.schedule
                ? 'Pick the session to save.'
                : 'Answer all questions to save.'}
            </Typography>
          )}
        </Box>
      </Paper>
    </Box>
  )
}

export default ProgramObservation
